import { useEffect } from "react";

/**
 * Minimal SEO hook — sets document.title and updates common meta tags.
 * Also injects an optional JSON-LD block by id (removes on unmount).
 */
export function useSEO({ title, description, canonical, jsonLd, robots, jsonLdId = "gms-jsonld" }) {
  useEffect(() => {
    if (title) document.title = title;
    const robotsEl = document.querySelector("meta[name='robots']");
    if (robotsEl) robotsEl.setAttribute("content", robots || "index, follow, max-image-preview:large");
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}='${name}']`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (description) {
      setMeta("description", description);
      setMeta("og:title", title, "property");
      setMeta("og:description", description, "property");
      setMeta("og:type", "website", "property");
      setMeta("twitter:title", title);
      setMeta("twitter:description", description);
    }
    if (canonical) {
      setMeta("og:url", canonical, "property");
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
    let script = null;
    if (jsonLd) {
      script = document.getElementById(jsonLdId);
      if (!script) {
        script = document.createElement("script");
        script.id = jsonLdId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd);
    }
    return () => {
      if (jsonLd) {
        const s = document.getElementById(jsonLdId);
        if (s) s.remove();
      }
    };
  }, [title, description, canonical, robots, jsonLd ? JSON.stringify(jsonLd) : null, jsonLdId]); // eslint-disable-line react-hooks/exhaustive-deps
}
